/**
 * Shared word-timing primitives for translated caption rendering.
 *
 * Translated phrases carry phrase-level timing only (`{ text, ts, endTs }`)
 * — there's no per-word source-of-truth timestamp on the translation
 * side. Two consumers need to derive per-word time from a phrase:
 *
 *  - `translatedKaraokeAnimation.ts` (PLAT-9622): builds renderer-source
 *    units so the canvas highlight advances word-by-word.
 *  - `syntheticCaptionBreaks.ts` (PLAT-9623): places a user-added line
 *    break at a text position and resolves its absolute timestamp.
 *
 * Both must agree on the same heuristic so the highlight cursor crosses
 * a synthetic break exactly when it crosses the underlying text
 * position. This module is the single source of truth.
 */

import {
  containsWideChar,
  isCjkOpeningPunctuation,
  splitSpacelessScriptCharacters,
  visualTextWidth,
} from '@src/utility/strings';

export type SplitStrategy = 'character-weighted' | 'equal-weighted';

export const DEFAULT_SPLIT_STRATEGY: SplitStrategy = 'character-weighted';

const WHITESPACE_OR_WORD_SPLIT = /(\s+)/;

export type PhraseWordTokens = {
  /** Pieces in original order — words and whitespace runs interleaved. */
  pieces: string[];
  /** Trimmed non-empty pieces — i.e. words only, in order. */
  words: string[];
};

// Lazily-created shared segmenter (PLAT-11480). The requested locale is
// deliberately unspecified: ICU segments CJK text with its dictionaries
// regardless of locale, and no tokenize call site carries the caption
// language. `null` = environment without Intl.Segmenter (fall back to the
// whitespace tokenizer, i.e. the pre-PLAT-11480 behavior).
let wordSegmenter: Intl.Segmenter | null | undefined;
function getWordSegmenter(): Intl.Segmenter | null {
  if (wordSegmenter === undefined) {
    wordSegmenter =
      typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
        ? new Intl.Segmenter(undefined, { granularity: 'word' })
        : null;
  }
  return wordSegmenter;
}

/**
 * PLAT-11480 — locale-aware tokenization for spaceless (CJK) text. Keeps
 * the `PhraseWordTokens` contract of the whitespace tokenizer: pieces
 * reconstruct the text exactly, whitespace runs are the only separators,
 * and punctuation rides attached to the preceding word (the way "mundo."
 * is one word on the Latin path) so re-joined rows never drop it. Opening
 * brackets (`「`) are the exception: they ride on the FOLLOWING word
 * (PLAT-11836), so no row ever ends with one.
 */
function tokenizeWithSegmenter(
  text: string,
  segmenter: Intl.Segmenter,
): PhraseWordTokens {
  const pieces: string[] = [];
  const words: string[] = [];
  let run = '';
  let runIsOpeners = false; // run holds only opening brackets so far
  const flushRun = () => {
    if (run.length === 0) return;
    pieces.push(run);
    words.push(run);
    run = '';
    runIsOpeners = false;
  };
  for (const { segment, isWordLike } of segmenter.segment(text)) {
    if (segment.trim().length === 0) {
      flushRun();
      pieces.push(segment);
    } else if (isWordLike) {
      if (runIsOpeners) {
        run += segment;
        runIsOpeners = false;
      } else {
        flushRun();
        run = segment;
      }
    } else if (isCjkOpeningPunctuation(segment)) {
      if (!runIsOpeners) flushRun();
      run += segment;
      runIsOpeners = true;
    } else {
      // Punctuation/symbols attach to the preceding word run; with no
      // preceding word they open a run of their own (Latin equivalence:
      // a standalone "," is its own word after a whitespace split).
      run += segment;
    }
  }
  flushRun();
  return { pieces, words };
}

/**
 * PLAT-11481 — each word's `[start, end)` char offsets into `text`, in
 * order, derived from `tokenizePhraseWords` pieces (which reconstruct the
 * text exactly). Shared by the caption re-text distribution and the
 * over-length line splitter so both agree on word boundaries.
 */
export function computeWordSpans(
  text: string,
): Array<{ start: number; end: number }> {
  const spans: Array<{ start: number; end: number }> = [];
  let offset = 0;
  for (const piece of tokenizePhraseWords(text).pieces) {
    if (piece.trim().length > 0) {
      spans.push({ start: offset, end: offset + piece.length });
    }
    offset += piece.length;
  }
  return spans;
}

/**
 * PLAT-11836 — `[start, end)` spans of the units a caption row may break
 * between: `computeWordSpans` words, with spaceless-script words split per
 * character (`splitSpacelessScriptCharacters`). Latin words stay whole.
 */
export function computeCaptionUnitSpans(
  text: string,
): Array<{ start: number; end: number }> {
  const spans: Array<{ start: number; end: number }> = [];
  for (const word of computeWordSpans(text)) {
    let start = word.start;
    for (const unit of splitSpacelessScriptCharacters(
      text.slice(word.start, word.end),
    )) {
      spans.push({ start, end: start + unit.length });
      start += unit.length;
    }
  }
  return spans;
}

export function tokenizePhraseWords(text: string): PhraseWordTokens {
  // Full-width text has no whitespace to split on — the whitespace path
  // returns the whole phrase as one "word", which collapsed the karaoke
  // highlight to whole-line and made multi-row re-texting bail out.
  if (containsWideChar(text)) {
    const segmenter = getWordSegmenter();
    if (segmenter) return tokenizeWithSegmenter(text, segmenter);
  }
  const pieces = text.split(WHITESPACE_OR_WORD_SPLIT).filter(p => p.length > 0);
  const words = pieces.filter(p => p.trim().length > 0);
  return { pieces, words };
}

/**
 * Distribute `phraseDuration` across `words` under the given strategy.
 *
 * - `'equal-weighted'`: `phraseDuration / wordCount` per word.
 * - `'character-weighted'`: each word's slice is proportional to its
 *   character count (`phraseDuration * word.length / totalChars`).
 *
 * Empty input returns an empty array. If `words` are all zero-length
 * (degenerate input), falls back to equal weighting so we don't divide
 * by zero.
 */
export function computeWordDurations(
  words: readonly string[],
  phraseDuration: number,
  strategy: SplitStrategy = DEFAULT_SPLIT_STRATEGY,
): number[] {
  if (words.length === 0) return [];
  if (strategy === 'equal-weighted') {
    const perWord = phraseDuration / words.length;
    return words.map(() => perWord);
  }
  // Visual width, not code units (PLAT-11480): a full-width character is
  // ~2 Latin widths, so mixed CJK/Latin phrases weight sensibly. For pure
  // Latin/Cyrillic text visual width === length — behavior unchanged.
  const totalChars = words.reduce((sum, w) => sum + visualTextWidth(w), 0);
  if (totalChars === 0) {
    const perWord = phraseDuration / words.length;
    return words.map(() => perWord);
  }
  return words.map(w => phraseDuration * (visualTextWidth(w) / totalChars));
}

/**
 * Given a char offset into the phrase text, return the proportion
 * `[0, 1]` of the phrase's time window that has elapsed at that
 * position under the given strategy.
 *
 * An offset inside a word takes that word's share up to the offset, by
 * visual width (PLAT-11836: a Japanese caption breaks between any two
 * characters, not only at dictionary-word boundaries).
 */
export function computeProportionAtTextPosition(
  text: string,
  textPosition: number,
  strategy: SplitStrategy = DEFAULT_SPLIT_STRATEGY,
): number {
  if (textPosition <= 0) return 0;
  if (textPosition >= text.length) return 1;

  const { pieces, words } = tokenizePhraseWords(text);
  if (words.length === 0) return 0;

  const wordDurations = computeWordDurations(words, 1, strategy);

  let pos = 0;
  let wordIdx = 0;
  let cumulative = 0;
  for (const piece of pieces) {
    const pieceEnd = pos + piece.length;
    if (pieceEnd > textPosition) {
      if (piece.trim().length > 0) {
        const before = visualTextWidth(piece.slice(0, textPosition - pos));
        cumulative +=
          wordDurations[wordIdx] * (before / visualTextWidth(piece));
      }
      break;
    }
    if (piece.trim().length > 0) {
      cumulative += wordDurations[wordIdx];
      wordIdx += 1;
    }
    pos = pieceEnd;
  }
  return Math.max(0, Math.min(1, cumulative));
}
